// 일괄 등록 모달
import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Grid,
  Stack,
  useToast,
  Select,
  Checkbox,
  Text,
  Box,
  Divider,
  Badge
} from '@chakra-ui/react';
import { useAuth } from 'contexts/AuthContext';
import {
  createWorkSchedule,
  getLocations,
  getAllSemesters
} from 'utils/firestoreUtils';

const DAYS_OF_WEEK = [
  { value: 0, label: '일요일' },
  { value: 1, label: '월요일' },
  { value: 2, label: '화요일' },
  { value: 3, label: '수요일' },
  { value: 4, label: '목요일' },
  { value: 5, label: '금요일' },
  { value: 6, label: '토요일' }
];

function BulkScheduleModal({ isOpen, onClose, onSaved }) {
  const { currentUser } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [semesters, setSemesters] = useState([]);

  const [formData, setFormData] = useState({
    semesterId: '',
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    requiredHeadcount: 1,
    location: '',
    description: '',
    wageAmount: '10320',
    selectedDays: [1, 2, 3, 4, 5] // 기본: 월~금
  });

  useEffect(() => {
    loadLocations();
    loadSemesters();
  }, []);

  const loadLocations = async () => {
    try {
      const locs = await getLocations();
      setLocations(locs);
    } catch (error) {
      console.error('위치 조회 오류:', error);
    }
  };

  const loadSemesters = async () => {
    try {
      const sems = await getAllSemesters();
      setSemesters(sems);
    } catch (error) {
      console.error('학기 조회 오류:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDayToggle = (dayValue) => {
    setFormData(prev => ({
      ...prev,
      selectedDays: prev.selectedDays.includes(dayValue)
        ? prev.selectedDays.filter(d => d !== dayValue)
        : [...prev.selectedDays, dayValue].sort((a, b) => a - b)
    }));
  };

  const handleBulkCreate = async () => {
    if (!formData.semesterId || !formData.startDate || !formData.endDate || 
        !formData.startTime || !formData.endTime || !formData.location) {
      toast({
        title: '입력 오류',
        description: '학기를 포함한 모든 필수 항목을 입력해주세요.',
        status: 'warning',
        duration: 3000
      });
      return;
    }

    if (formData.selectedDays.length === 0) {
      toast({
        title: '입력 오류',
        description: '최소 한 개 이상의 요일을 선택해주세요.',
        status: 'warning',
        duration: 3000
      });
      return;
    }

    try {
      setLoading(true);

      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      
      if (startDate > endDate) {
        toast({
          title: '입력 오류',
          description: '시작일이 종료일보다 늦을 수 없습니다.',
          status: 'warning',
          duration: 3000
        });
        return;
      }

      const schedules = [];
      const currentDate = new Date(startDate);

      // 날짜 범위 내의 모든 날짜 순회
      while (currentDate <= endDate) {
        const dayOfWeek = currentDate.getDay();
        
        // 선택된 요일인 경우만 추가
        if (formData.selectedDays.includes(dayOfWeek)) {
          const dateString = currentDate.toISOString().split('T')[0];
          schedules.push({
            semesterId: formData.semesterId,
            workDate: dateString,
            startTime: formData.startTime,
            endTime: formData.endTime,
            requiredHeadcount: formData.requiredHeadcount,
            location: formData.location,
            description: formData.description,
            wageAmount: formData.wageAmount
          });
        }

        currentDate.setDate(currentDate.getDate() + 1);
      }

      if (schedules.length === 0) {
        toast({
          title: '생성할 스케줄 없음',
          description: '선택한 기간과 요일에 해당하는 날짜가 없습니다.',
          status: 'warning',
          duration: 3000
        });
        return;
      }

      // 모든 스케줄 생성
      for (const schedule of schedules) {
        await createWorkSchedule(schedule, currentUser.uid);
      }

      toast({
        title: '일괄 생성 완료',
        description: `${schedules.length}개의 스케줄이 생성되었습니다.`,
        status: 'success',
        duration: 3000
      });

      onSaved();
      onClose();
      
      // 폼 초기화
      setFormData({
        semesterId: '',
        startDate: '',
        endDate: '',
        startTime: '',
        endTime: '',
        requiredHeadcount: 1,
        location: '',
        description: '',
        wageAmount: '10320',
        selectedDays: [1, 2, 3, 4, 5]
      });
    } catch (error) {
      console.error('일괄 생성 오류:', error);
      toast({
        title: '생성 실패',
        description: error.message || '스케줄 일괄 생성에 실패했습니다.',
        status: 'error',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>스케줄 일괄 등록</ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <Stack spacing={4}>
            <Box p={3} bg="blue.50" borderRadius="md">
              <Text fontSize="sm" color="blue.700">
                선택한 기간과 요일에 맞춰 여러 개의 스케줄을 한 번에 생성합니다.
              </Text>
            </Box>

            <FormControl isRequired>
              <FormLabel>학기</FormLabel>
              <Select
                value={formData.semesterId}
                onChange={(e) => handleChange('semesterId', e.target.value)}
                placeholder="학기를 선택하세요"
              >
                {semesters.map(sem => (
                  <option key={sem.id} value={sem.id}>
                    {sem.semesterName} ({sem.startDate} ~ {sem.endDate})
                  </option>
                ))}
              </Select>
            </FormControl>

            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <FormControl isRequired>
                <FormLabel>시작일</FormLabel>
                <Input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleChange('startDate', e.target.value)}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>종료일</FormLabel>
                <Input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => handleChange('endDate', e.target.value)}
                />
              </FormControl>
            </Grid>

            <FormControl isRequired>
              <FormLabel>반복 요일 선택</FormLabel>
              <Grid templateColumns="repeat(4, 1fr)" gap={2}>
                {DAYS_OF_WEEK.map(day => (
                  <Checkbox
                    key={day.value}
                    isChecked={formData.selectedDays.includes(day.value)}
                    onChange={() => handleDayToggle(day.value)}
                  >
                    {day.label}
                  </Checkbox>
                ))}
              </Grid>
            </FormControl>

            <Divider />

            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <FormControl isRequired>
                <FormLabel>시작시간</FormLabel>
                <Input
                  type="time"
                  value={formData.startTime}
                  onChange={(e) => handleChange('startTime', e.target.value)}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>종료시간</FormLabel>
                <Input
                  type="time"
                  value={formData.endTime}
                  onChange={(e) => handleChange('endTime', e.target.value)}
                />
              </FormControl>
            </Grid>

            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <FormControl isRequired>
                <FormLabel>근무지</FormLabel>
                <Select
                  value={formData.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  placeholder="선택하세요"
                >
                  {locations.map(loc => (
                    <option key={loc} value={loc}>{loc}</option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>필요 인원</FormLabel>
                <NumberInput
                  value={formData.requiredHeadcount}
                  min={1}
                  onChange={(_, value) => handleChange('requiredHeadcount', value)}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            </Grid>

            <FormControl isRequired>
              <FormLabel>시급 (원)</FormLabel>
              <NumberInput
                value={formData.wageAmount}
                min={0}
                step={10}
                onChange={(_, value) => handleChange('wageAmount', value)}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>

            <FormControl>
              <FormLabel>업무 설명</FormLabel>
              <Textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="업무 내용을 입력하세요"
                rows={3}
              />
            </FormControl>
          </Stack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            취소
          </Button>
          <Button
            colorScheme="green"
            onClick={handleBulkCreate}
            isLoading={loading}
          >
            일괄 생성
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default BulkScheduleModal;
